/**
 * Calculates room difficulty based on housekeeping, maintenance requirements, and item difficulties
 * @param {Object} room - Room object with housekeeping, maintenance, and item count data
 * @param {Array} items - Array of all items with difficulty values (optional)
 * @returns {number} - Numeric difficulty score
 */
export const calculateRoomDifficulty = (room, items = []) => {
  if (!room) return 0;
  console.log(`calculating room difficulty for room: ${room.displayName}`);
  // console.log(room);
  // console.log(items);

  const housekeeping =
    room.selectedHousekeepingRequirements?.toLowerCase() || "please select";
  const maintenance =
    room.selectedMaintenanceRequirements?.toLowerCase() || "please select";

  const roomStatus = room.status;

  // Skip calculation for rooms that are up or occupied
  if (
    roomStatus === "Up" ||
    roomStatus === "Occupied"
    // housekeeping === "completed" ||
    // maintenance === "completed" ||
    // housekeeping === "please select" ||
    // maintenance === "please select"
  ) {
    return 0;
  }

  // Base difficulty points from requirements
  let housekeepingPoints = 0;
  let maintenancePoints = 0;

  // Base points for housekeeping (minutes)
  const housekeepingWeights = {
    refresh: 15,
    easy: 45,
    medium: 90,
    heavy: 120,
    "very heavy": 180,
  };

  // Base points for maintenance (minutes)
  const maintenanceWeights = {
    easy: 30,
    medium: 60,
    heavy: 90,
    "very heavy": 120,
  };

  housekeepingPoints += housekeepingWeights[housekeeping] || 0;
  maintenancePoints += maintenanceWeights[maintenance] || 0;

  // Add item difficulty points if items are provided
  if (items && items.length > 0) {
    console.log(`Processing ${items.length} items for difficulty calculation`);

    // Create items dictionary for quick lookup by itemID
    const itemsDict = {};
    items.forEach((item) => {
      if (item.itemID) {
        itemsDict[item.itemID] = item;
        console.log(
          `Added item to dict: ${item.itemID} -> ${item.itemName} (difficulty: ${item.itemDifficulty})`
        );
      }
    });

    console.log("Items dictionary created:", Object.keys(itemsDict));

    // Calculate difficulty from ALL item count fields
    Object.keys(room).forEach((key) => {
      if (key.startsWith("itemCount") && room[key] > 0) {
        console.log(`Found room field: ${key} = ${room[key]}`);

        // Find the corresponding item using the exact key
        const item = itemsDict[key];

        if (item && item.itemDifficulty) {
          const difficulty = parseInt(item.itemDifficulty, 10) || 0;
          const count = parseInt(room[key], 10) || 0;
          const itemPoints = difficulty * count;

          console.log(`✅ MATCH FOUND: ${key}`);
          console.log(`   Item: ${item.itemName}`);
          console.log(
            `   Difficulty: ${difficulty} × Count: ${count} = ${itemPoints} points`
          );
          console.log(`   Responsibility: ${item.itemResponsibility}`);

          // Add to appropriate category based on responsibility (only if explicitly set)
          if (item.itemResponsibility === "Housekeeping") {
            housekeepingPoints += itemPoints;
            console.log(
              `   Added ${itemPoints} to housekeeping (new total: ${housekeepingPoints})`
            );
          } else if (item.itemResponsibility === "Maintenance") {
            maintenancePoints += itemPoints;
            console.log(
              `   Added ${itemPoints} to maintenance (new total: ${maintenancePoints})`
            );
          } else {
            // If no responsibility specified, don't add to either (matches DRL behavior)
            console.log(
              `   Skipped (no responsibility set) - not added to any total`
            );
          }
        } else {
          console.log(`❌ NO MATCH: ${key} - item not found or no difficulty`);
          if (!item) {
            console.log(`   Item not found in dictionary`);
          } else if (!item.itemDifficulty) {
            console.log(
              `   Item found but no itemDifficulty: ${JSON.stringify(item)}`
            );
          }
        }
      }
    });
  }

  const totalPoints = housekeepingPoints + maintenancePoints;

  console.log(`=== DIFFICULTY CALCULATION SUMMARY for ${room.displayName} ===`);
  console.log(
    `Base Housekeeping (${housekeeping}): ${
      housekeepingWeights[housekeeping] || 0
    } points`
  );
  console.log(
    `Base Maintenance (${maintenance}): ${maintenanceWeights[maintenance] || 0} points`
  );
  console.log(`Total Housekeeping Points: ${housekeepingPoints}`);
  console.log(`Total Maintenance Points: ${maintenancePoints}`);
  console.log(`FINAL TOTAL: ${totalPoints}`);
  console.log(`===============================================`);

  return totalPoints;
};

/**
 * Gets difficulty level string based on numeric score (for backwards compatibility)
 * @param {number} difficulty - Numeric difficulty score
 * @returns {string} - Difficulty level string
 */
export const getDifficultyLevel = (difficulty) => {
  if (difficulty === 0) return "Not Set";
  if (difficulty <= 60) return "Easy";
  if (difficulty <= 120) return "Medium";
  if (difficulty <= 200) return "Hard";
  return "Very Hard";
};

/**
 * Gets color coding for difficulty level
 * @param {string|number} difficulty - Difficulty level (string) or score (number)
 * @returns {string} - Color code
 */
export const getDifficultyColor = (difficulty) => {
  // If it's a number, convert to difficulty level first
  if (typeof difficulty === "number") {
    difficulty = getDifficultyLevel(difficulty);
  }

  const colors = {
    Easy: "#4caf50", // Green
    Medium: "#ff9800", // Orange
    Hard: "#f44336", // Red
    "": "#9c27b0", // Purple
    "Not Set": "#9e9e9e", // Grey
  };
  return colors[difficulty] || colors["Not Set"];
};

/**
 * Gets color coding directly from numeric difficulty score
 * @param {number} difficultyScore - Numeric difficulty score
 * @returns {string} - Color code
 */
export const getDifficultyColorFromScore = (difficultyScore) => {
  if (difficultyScore === 0) return "#9e9e9e"; // Grey - Not Set
  if (difficultyScore <= 60) return "#4caf50"; // Green - Easy
  if (difficultyScore <= 120) return "#ff9800"; // Orange - Medium
  if (difficultyScore <= 200) return "#f44336"; // Red - Hard
  return "#9c27b0"; // Purple - Very Hard
};

/**
 * Calculates room cost based on item costs and quantities
 * @param {Object} room - Room object with item count data
 * @param {Array} items - Array of all items with cost values
 * @returns {number} - Total cost in dollars
 */
export const calculateRoomCost = (room, items = []) => {
  if (!room) return 0;
  console.log(`calculating room cost for room: ${room.displayName}`);

  const roomStatus = room.status;

  // Skip calculation for rooms that are up or occupied
  if (roomStatus === "Up" || roomStatus === "Occupied") {
    return 0;
  }

  let totalCost = 0;

  // Add item cost points if items are provided
  if (items && items.length > 0) {
    console.log(`Processing ${items.length} items for cost calculation`);

    // Create items dictionary for quick lookup by itemID
    const itemsDict = {};
    items.forEach((item) => {
      if (item.itemID) {
        itemsDict[item.itemID] = item;
        console.log(
          `Added item to dict: ${item.itemID} -> ${item.itemName} (cost: ${item.itemCost})`
        );
      }
    });

    console.log("Items dictionary created:", Object.keys(itemsDict));

    // Calculate cost from ALL item count fields
    Object.keys(room).forEach((key) => {
      if (key.startsWith("itemCount") && room[key] > 0) {
        console.log(`Found room field: ${key} = ${room[key]}`);

        // Find the corresponding item using the exact key
        const item = itemsDict[key];

        if (item && item.itemCost) {
          const cost = parseFloat(item.itemCost) || 0;
          const count = parseInt(room[key], 10) || 0;
          const itemCost = cost * count;

          console.log(`✅ COST MATCH FOUND: ${key}`);
          console.log(`   Item: ${item.itemName}`);
          console.log(
            `   Cost: $${cost} × Count: ${count} = $${itemCost}`
          );

          totalCost += itemCost;
        } else {
          console.log(`❌ NO COST MATCH: ${key} - item not found or no cost`);
          if (!item) {
            console.log(`   Item not found in dictionary`);
          } else if (!item.itemCost) {
            console.log(
              `   Item found but no itemCost: ${JSON.stringify(item)}`
            );
          }
        }
      }
    });
  }

  console.log(`=== COST CALCULATION SUMMARY for ${room.displayName} ===`);
  console.log(`TOTAL COST: $${totalCost.toFixed(2)}`);
  console.log(`===============================================`);

  return totalCost;
};
